import {Fragment, useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {BreadCrumb} from "../../component/Breadcrumb";
import {get, post} from "../../js/api";
import {markDirty, showError, showMessage} from "../../js/main";
import {OrderInformation} from "../../component/OrderInformation";
import {Button} from "../../component/form/Button";
import {germanDate, RefreshSignal, useChangeEffect} from "../../js/util";
import {OrderCorrespondence, prepareOrderCorrespondenceMessage} from "../../component/OrderCorrespondence";
import {isOrderAwaitingProcessing, isOrderProcessing, orderState, orderType, paymentStateLabels, paymentStates} from "../../js/constants";

const refresh = new RefreshSignal();

const getOrderWording = (order)=>{
    return order?.type === orderType.standard ? "Bestellung " : "Anfrage ";
}

export const mapState = paymentState => {
    return paymentStateLabels.get(paymentState) ?? "Unbekannt";
}

export const UpdateOrder = ({orders}) => {

    const {orderId} = useParams();

    const [editmode, setEditmode] = useState(false);
    const [reply, setReply] = useState();

    const [selectedOrder, setSelectedOrder] = useState();
    const [paymentInfo, setPaymentInfo] = useState();
    const [referencingOrderInfo, setReferencingOrderInfo] = useState();
    const [isSubmitting, setSubmitting] = useState(false);

    const formRef = useRef();


    useEffect(() => {
        if (orders) {
            const order = orders.find(o => o.id === parseInt(orderId));
            if(order) {
                setSelectedOrder(order);
            }
        }
        get("/orders/" + orderId + "?detailed=true").then(detailed =>{
            setSelectedOrder(detailed.orderInfo);
            setPaymentInfo(detailed.paymentInfo);
            setReferencingOrderInfo(detailed.referencingOrderInfo);
        });
    }, [orders, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    useChangeEffect(() => {
        setEditmode(false);
        refresh.changed();
    }, [orders])

    const history = useHistory();

    const acceptOrder = () => {
        setSubmitting(true);
        post("/orders/" + orderId + "/acceptOrder").then(() => {
            markDirty();
            showMessage("Die "+getOrderWording(selectedOrder)+" wurde als \"in Bearbeitung\" markiert.");
        }).finally(() => {
            setSubmitting(false);
        });
    }

    const completeOrder = () => {
        setSubmitting(true);
        post("/orders/" + orderId + "/completeOrder").then(() => {
            markDirty();
            showMessage("Die "+getOrderWording(selectedOrder)+" wurde erfolgreich abgeschlossen.");
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const reopenOrder = () => {
        setSubmitting(true);
        post("/orders/" + orderId + "/reopenOrder").then(() => {
            markDirty();
            showMessage("Die "+getOrderWording(selectedOrder)+" wurde erfolgreich wiedereröffnet.");
        }).finally(() => {
            setSubmitting(false);
        });
    }

    const updateOrder = async () => {
        setSubmitting(true);
        try {
            const body = {order: selectedOrder, message: await prepareOrderCorrespondenceMessage(reply, false)};
            await post(`/orders/${orderId}/updateOrder`, body);
            markDirty();
            showMessage("Die "+getOrderWording(selectedOrder)+" wurde erfolgreich bearbeitet");
        } catch (e) {
            showError("Die "+getOrderWording(selectedOrder)+" konnte nicht gespeichert werden.");
        } finally {
            setSubmitting(false);
        }
    }

    const cancelOrder = () => {
        if (!window.confirm("Wirklich stornieren?")) {
            return;
        }
        setSubmitting(true);
        post("/orders/" + orderId + "/cancelOrder").then(() => {
            markDirty();
            showMessage("Die "+getOrderWording(selectedOrder)+" wurde erfolgreich storniert.");
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const sendMessage = async () => {
        if(!formRef.current.checkValidity()){
            return;
        }

        setSubmitting(true);
        try {
            const message = await prepareOrderCorrespondenceMessage(reply, false);
            await post(`/orders/${orderId}/correspondence`, message);
            markDirty();
            showMessage("Die Nachricht wurde gesendet.")
            refresh.changed();
        } catch (e) {
            showError("Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
        } finally {
            setSubmitting(false);
        }
    }

    const cancelable_states = ["CREATED", "OPEN", "PAID", "IN_PROGRESS", "IN_PRODUCTION", "AWAIT_PAYMENT"];

    const updateable_states = ["CREATED", "IN_PROGRESS", "IN_PRODUCTION"]

    const bearbeitungSpeichernButton = <Button key="save" theme="green mr-1 mb-1" width={230} submitting={isSubmitting}
                                               onClick={() => formRef.current.checkValidity() ? updateOrder() : null}>Bearbeitung speichern</Button>;

    const getButtons = ({state}) => {
        const content = [];

        if(editmode) {
            switch (state){
                case "IN_PROGRESS":
                case "IN_PRODUCTION":
                    content.push(<Button key="trash" theme="red mr-1 mb-1" width={230} submitting={isSubmitting}
                                         onClick={() => window.location.reload()} type="button">Bearbeitung verwerfen</Button>);
                    content.push(bearbeitungSpeichernButton);
                    break;
            }
        }else{
            if (isOrderProcessing(state)) {
                content.push(<Button key="complete" theme="green mr-1 mb-1" width={230} submitting={isSubmitting}
                                     onClick={completeOrder} type="button">{getOrderWording(selectedOrder)} abschließen</Button>);
            } else if (isOrderAwaitingProcessing(state) || state === "AWAIT_USER" || state == orderState.CREATED) {
                content.push(<Button key="accept" theme="green mr-1 mb-1" width={230} submitting={isSubmitting}
                                     onClick={acceptOrder} type="button">{getOrderWording(selectedOrder)}  annehmen</Button>)
            } else if (state === "AWAIT_PAYMENT") {
                content.push(<Button key="accept2" theme="green mr-1 mb-1" width={230} submitting={isSubmitting}
                                     onClick={() => {
                                         if(window.confirm("Übernehmen dieser Bestellung führt zu einem Abbruch der Kundenbezahlung per Bezahllink. Trotzdem übernehmen?")){
                                             acceptOrder();
                                         }
                                     }} type="button">{getOrderWording(selectedOrder)}  annehmen</Button>)
            } else if (state === 'CANCELED'){
                content.push(<Button key="accept3" theme="green mr-1 mb-1" width={230} submitting={isSubmitting} onClick={reopenOrder}
                                     type="button">{getOrderWording(selectedOrder)}  wiedereröffnen</Button>)
            }
            if(updateable_states.includes(state)){
                content.push(<Button key="alter" theme="green mr-1 mb-1" width={230} submitting={isSubmitting}
                                     onClick={() => setEditmode(true)} type="button">{getOrderWording(selectedOrder)}  bearbeiten</Button>);
            }
            if (cancelable_states.includes(state)) {
                content.push(<Button key="cancel" theme="red mr-1 mb-1" width={230} submitting={isSubmitting}
                                     onClick={cancelOrder}>{getOrderWording(selectedOrder)}  stornieren</Button>);
            }

        }


        return content;
    }

    const resendPaymentLink = () => {
        setSubmitting(true);
        post(`/orders/${selectedOrder.id}/resendPaymentLink`).then((response) => {
            showMessage("Bezahllink wurde erneut versendet");
        }).finally(() => setSubmitting(false))
    }


    return (
        <Fragment>
            {selectedOrder && <div>
                <BreadCrumb currentTitle={getOrderWording(selectedOrder) + orderId} parentTitle="Bestellungen"
                            parentLink="/bestellungen"/>

                <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
                    <div className="no-print">
                        {getButtons(selectedOrder)}
                    </div>

                    <OrderInformation order={selectedOrder} referencingOrderInfo={referencingOrderInfo} showUser
                                      adminView updateOrderFunction={editmode ? setSelectedOrder : null}/>
                    {paymentInfo && <>
                        <h2 className="mt-1d5 mb-03">Bezahlinformationen</h2>

                        <div className="default">
                            <table>
                                <tbody>
                                    <tr>
                                        <td width="200">Bezahlung durch</td>
                                        <td>{paymentInfo.payer}</td>
                                    </tr>
                                    <tr>
                                        <td>Status</td>
                                        <td>{mapState(paymentInfo.paymentState)}</td>
                                    </tr>
                                    {paymentInfo.intentId &&
                                        <tr>
                                            <td>Stripe Intent-ID</td>
                                            <td>{paymentInfo.intentId}</td>
                                        </tr>}
                                    {paymentInfo.intentResponseDate && <tr>
                                        <td>Stripe Bestätigungszeitpunkt</td>
                                        <td>{germanDate(paymentInfo.intentResponseDate)}</td>
                                    </tr>}
                                </tbody>
                            </table>
                            {[paymentStates.AWAIT_USER, paymentStates.FAILED, paymentStates.CANCELED].indexOf(paymentInfo.paymentState) !== -1
                                && [orderState.OPEN, orderState.PAID, orderState.IN_PROGRESS, orderState.IN_PRODUCTION, orderState.AWAIT_PAYMENT, orderState.AWAIT_USER].indexOf(selectedOrder.state) !== -1
                                && <div className="no-print">
                                <Button key="sendpayment" theme="blue mr-1 mb-1" width={230} submitting={isSubmitting}
                                        onClick={resendPaymentLink} type="button">Bezahllink senden</Button>
                            </div>}
                        </div>

                    </>}

                    <OrderCorrespondence identifier={selectedOrder.id} onCreateReply={setReply}
                                         adminView={true} refresh={refresh}/>
                    <div className={"safe-actions mt-1 no-print"}>
                        {editmode && bearbeitungSpeichernButton}
                        {!editmode && reply?.type &&
                            <Button key="sendmsg" theme="blue mr-1 mb-1" width={230} submitting={isSubmitting}
                                    onClick={() => formRef.current.checkValidity() ? sendMessage() : null} type="button">Nachricht senden</Button>
                        }
                    </div>
                </form>
            </div>}
        </Fragment>
    );

}
